"use client"

import React, { useMemo, useEffect } from "react"
import { Button } from "@/components/ui/button"

/** JSONレコードの型定義 */
interface PublicBuildingData {
  公共建築名: string
  建設場所: string
  設計者: string
  概要: string
  タイプ: string // ← 新しく追加されたフィールド
}

interface QuizQuestionProps {
  buildingData: PublicBuildingData
  allBuildings: PublicBuildingData[]
  onChoice: (choiceName: string) => void
  onChoicesGenerated: (choices: PublicBuildingData[]) => void
  isAnswered: boolean
}

export function QuizQuestion({
  buildingData,
  allBuildings,
  onChoice,
  onChoicesGenerated,
  isAnswered,
}: QuizQuestionProps) {
  /**
   * 4つの選択肢を生成 (正解 + 3つの誤答)
   * - 同じタイプの建物だけを候補とする
   */
  const choices = useMemo(() => {
    // 1. 問題(正解)のタイプを取得
    const correctType = buildingData.タイプ

    // 2. 同じタイプの全データを集める
    const sameTypeBuildings = allBuildings.filter(
      (b) => b.タイプ === correctType,
    )

    // 3. 「正解」以外を抽出してシャッフル
    const incorrect = sameTypeBuildings.filter(
      (b) => b.公共建築名 !== buildingData.公共建築名,
    )
    shuffleArray(incorrect)

    // 4. 誤答3つ & 正解1つ → 合計4
    const selected = [buildingData, ...incorrect.slice(0, 3)]
    shuffleArray(selected)

    return selected
  }, [buildingData, allBuildings])

  // 親へ4択のデータを通知
  useEffect(() => {
    onChoicesGenerated(choices)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choices])

  /**
   * 回答ボタン
   */
  const handleClick = (choice: PublicBuildingData) => {
    if (!isAnswered) {
      onChoice(choice.公共建築名)
    }
  }

  return (
    <div>
      <div className="mb-4">
        <p className="font-semibold">【概要】</p>
        <p>{buildingData.概要}</p>
      </div>

      <div className="flex flex-col gap-2">
        {choices.map((c) => (
          <Button
            key={c.公共建築名}
            variant="outline"
            onClick={() => handleClick(c)}
            disabled={isAnswered}
          >
            {c.公共建築名}
          </Button>
        ))}
      </div>
    </div>
  )
}

/**
 * シャッフル用ヘルパー関数
 */
function shuffleArray<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const r = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[r]] = [arr[r], arr[i]]
  }
}
